import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  Settings, Users, UserPlus, Trash2, Crown, ExternalLink, Check, X, 
  Loader2, Globe, Lock, MessageCircle, ArrowRight, ShoppingCart, RefreshCw
} from "lucide-react";
import UserProfileCard from "./UserProfileCard";
import { purchaseProduct, isNativePlatform } from "../utils/revenuecat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Retry helper: retries a fn up to maxRetries times with delay
const retryAsync = async (fn, maxRetries = 3, delayMs = 1500) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (attempt === maxRetries) throw e;
      await new Promise(r => setTimeout(r, delayMs * attempt));
    }
  }
};

export default function LeagueAdminPanel({ league, onUpdate, onClose }) {
  const { getAuthHeader } = useAuth();
  
  const [activeTab, setActiveTab] = useState("members");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isNative, setIsNative] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Settings state
  const [name, setName] = useState(league?.name || "");
  const [description, setDescription] = useState(league?.description || "");
  const [visibility, setVisibility] = useState(league?.visibility || "private");
  const [whatsappLink, setWhatsappLink] = useState(league?.whatsapp_link || "");
  const [newAdminId, setNewAdminId] = useState("");
  
  // Join requests
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState(null);
  
  // Members
  const [kickingUserId, setKickingUserId] = useState(null);
  
  // Profile modal
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null);

  // Check native platform on mount
  useEffect(() => {
    const checkPlatform = async () => {
      const native = await isNativePlatform();
      setIsNative(native);
    };
    checkPlatform();
  }, []);

  // Force-refresh league data from backend
  const refreshLeague = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API}/leagues/${league.id}`, { headers });
      if (res.data) onUpdate(res.data);
    } catch (e) {
      console.error("Failed to refresh league:", e);
    }
  }, [league.id, getAuthHeader, onUpdate]);

  // Capacity upgrade handler with retry logic
  const handleCapacityUpgrade = async (productId, targetSize) => {
    if (!isNative) {
      setError("Capacity upgrades are available in the mobile app only.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await purchaseProduct(productId);
      
      // Record purchase with league_id
      const headers = getAuthHeader();
      await axios.post(`${API}/leagues/purchase`, {
        product_id: productId,
        transaction_id: `${productId}_${Date.now()}`,
        league_id: league.id,
      }, { headers });
      
      // Apply upgrade with retry (3 attempts)
      await retryAsync(async () => {
        await axios.put(`${API}/leagues/${league.id}`, { max_members: targetSize }, { headers });
      });
      
      // Force refresh league data
      await refreshLeague();
      setSuccess(`Upgraded to ${targetSize} members!`);
    } catch (e) {
      if (!e.cancelled) {
        setError(e.response?.data?.detail || `Upgrade failed. Use "Restore Purchase" below to re-apply.`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Restore purchase handler
  const handleRestorePurchase = async () => {
    setRestoring(true);
    setError("");
    try {
      const headers = getAuthHeader();
      const res = await axios.post(`${API}/leagues/${league.id}/restore-purchase`, {}, { headers });
      if (res.data.restored) {
        await refreshLeague();
        setSuccess(res.data.message);
      } else {
        setError(res.data.message || "No pending upgrades found.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to restore purchase.");
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    if (activeTab === "requests") {
      fetchRequests();
    }
  }, [activeTab]);

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const res = await axios.get(`${API}/leagues/${league.id}/requests`, { headers: getAuthHeader() });
      setRequests(res.data.requests || []);
    } catch (e) {
      console.error("Failed to fetch requests:", e);
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await axios.put(`${API}/leagues/${league.id}`, {
        name,
        description,
        visibility,
        whatsapp_link: whatsappLink || null
      }, { headers: getAuthHeader() });
      setSuccess("Settings saved!");
      if (onUpdate) onUpdate();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleKickMember = async (userId, displayName) => {
    if (!window.confirm(`Remove ${displayName} from the league?`)) return;
    setKickingUserId(userId);
    setError("");
    try {
      await axios.delete(`${API}/leagues/${league.id}/members/${userId}`, { headers: getAuthHeader() });
      setSuccess(`${displayName} has been removed from the league.`);
      if (onUpdate) onUpdate();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove member");
    } finally {
      setKickingUserId(null);
    }
  };

  const handleRequestAction = async (requestId, action) => {
    setProcessingRequestId(requestId);
    setError("");
    try {
      await axios.put(`${API}/leagues/${league.id}/requests/${requestId}`, { action }, { headers: getAuthHeader() });
      setSuccess(`Request ${action}ed!`);
      fetchRequests();
      if (action === "accept" && onUpdate) onUpdate();
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleTransferAdmin = async () => {
    if (!newAdminId) {
      setError("Select a member to transfer admin to");
      return;
    }
    if (!window.confirm("Are you sure you want to transfer admin role? You will become a regular member.")) return;
    setLoading(true);
    setError("");
    try {
      await axios.put(`${API}/leagues/${league.id}/transfer-admin`, { new_admin_id: newAdminId }, { headers: getAuthHeader() });
      setSuccess("Admin role transferred!");
      if (onClose) onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to transfer admin");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeague = async () => {
    if (!window.confirm("Are you sure you want to delete this league? This cannot be undone.")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/leagues/${league.id}`, { headers: getAuthHeader() });
      if (onClose) onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete league");
      setLoading(false);
    }
  };

  // Get members from league.leaderboard if available
  const members = league?.leaderboard || [];
  const otherMembers = members.filter(m => m.id !== league.created_by);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div 
        className="bg-slate-800 w-full max-w-lg max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Settings size={20} className="text-emerald-400" />
            League Admin
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          {["members", "requests", "settings"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-all ${
                activeTab === tab 
                  ? "text-emerald-400 border-b-2 border-emerald-400" 
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {tab === "members" && <Users className="inline mr-1" size={16} />}
              {tab === "requests" && <UserPlus className="inline mr-1" size={16} />}
              {tab === "settings" && <Settings className="inline mr-1" size={16} />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === "requests" && requests.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-emerald-500 text-white text-xs rounded-full">
                  {requests.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 text-sm">
              {success}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === "members" && (
            <div className="space-y-3">
              {/* Member Count Summary */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-emerald-400" />
                  <span className="text-white font-medium">Members</span>
                </div>
                <span className="text-lg font-bold text-white">
                  {members.length}<span className="text-slate-400">/{league.max_members || 5}</span>
                </span>
              </div>
              
              {/* Member List */}
              {members.map(member => (
                <div 
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                >
                  <div 
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => setSelectedProfileUserId(member.id)}
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium">
                      {(member.team_name || member.display_name)?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium flex items-center gap-1">
                        {member.team_name || member.display_name}
                        {member.id === league.created_by && (
                          <Crown size={12} className="text-amber-400" />
                        )}
                      </p>
                    </div>
                  </div>
                  {member.id !== league.created_by && (
                    <button
                      onClick={() => handleKickMember(member.id, member.team_name || member.display_name)}
                      disabled={kickingUserId === member.id}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                    >
                      {kickingUserId === member.id ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Requests Tab */}
          {activeTab === "requests" && (
            <div className="space-y-3">
              {requestsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-emerald-400" size={24} />
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlus className="mx-auto text-slate-600 mb-2" size={32} />
                  <p className="text-slate-400 text-sm">No pending join requests</p>
                </div>
              ) : (
                requests.map(req => (
                  <div key={req.id} className="p-3 bg-slate-900/50 rounded-lg">
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => setSelectedProfileUserId(req.user_id)}
                    >
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium">
                        {req.profile?.display_name?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{req.profile?.display_name || "Unknown"}</p>
                        <p className="text-slate-400 text-sm">{req.profile?.team_name || "No team"} • #{req.profile?.global_rank}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs">
                          <span className="text-emerald-400">{req.profile?.total_points} pts</span>
                          <span className="text-slate-500">|</span>
                          <span className={`capitalize ${
                            req.profile?.tier === "pro_max" ? "text-amber-400" :
                            req.profile?.tier === "pro_plus" ? "text-purple-400" :
                            req.profile?.tier === "pro" ? "text-blue-400" : "text-slate-400"
                          }`}>{req.profile?.tier?.replace("_", " ")}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleRequestAction(req.id, "accept")}
                        disabled={processingRequestId === req.id}
                        className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-all disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        {processingRequestId === req.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <>
                            <Check size={14} />
                            Accept
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleRequestAction(req.id, "reject")}
                        disabled={processingRequestId === req.id}
                        className="flex-1 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-600 transition-all disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        <X size={14} />
                        Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">League Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                  maxLength={50}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500 resize-none"
                  rows={3}
                  maxLength={500}
                  placeholder="Tell people about your league..."
                />
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">Visibility</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setVisibility("private")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      visibility === "private" 
                        ? "bg-emerald-600 text-white" 
                        : "bg-slate-700 text-slate-300"
                    }`}
                  >
                    <Lock size={14} />
                    Private
                  </button>
                  <button
                    onClick={() => setVisibility("public")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      visibility === "public" 
                        ? "bg-emerald-600 text-white" 
                        : "bg-slate-700 text-slate-300"
                    }`}
                  >
                    <Globe size={14} />
                    Public
                  </button>
                </div>
                <p className="text-slate-500 text-xs mt-1">
                  {visibility === "public" 
                    ? "Anyone can find and request to join this league" 
                    : "Only people with the invite code can join"}
                </p>
              </div>

              {/* WhatsApp Link */}
              <div>
                <label className="block text-slate-400 text-xs mb-1 flex items-center gap-1">
                  <MessageCircle size={12} />
                  WhatsApp Group Link (optional)
                </label>
                <input
                  type="url"
                  value={whatsappLink}
                  onChange={e => setWhatsappLink(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                  placeholder="https://chat.whatsapp.com/..."
                />
                <p className="text-slate-500 text-xs mt-1">Members will see a "Join WhatsApp" button</p>
              </div>

              {/* Member Capacity */}
              <div>
                <label className="block text-slate-400 text-xs mb-1 flex items-center gap-1">
                  <Users size={12} />
                  Member Capacity
                </label>
                <div className="flex items-center gap-2 p-3 bg-slate-900 border border-slate-700 rounded-lg mb-2">
                  <span className="text-white font-medium">{league.max_members || 5} members max</span>
                  <span className="text-slate-500">•</span>
                  <span className="text-slate-400 text-sm">{members.length} current</span>
                </div>
                
                {/* Inline Upgrade Options */}
                {(league.max_members || 5) < 50 && (
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <p className="text-slate-400 text-xs mb-2 flex items-center gap-1">
                      <ShoppingCart size={12} /> Upgrade Capacity
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {(league.max_members || 5) < 15 && (
                        <button
                          onClick={() => handleCapacityUpgrade("apl_league_size_15_v2", 15)}
                          disabled={saving}
                          className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-center hover:border-emerald-500 transition-all disabled:opacity-50"
                        >
                          <p className="text-emerald-400 font-bold text-sm">15</p>
                          <p className="text-slate-500 text-xs">AED 5</p>
                        </button>
                      )}
                      {(league.max_members || 5) < 30 && (
                        <button
                          onClick={() => handleCapacityUpgrade("apl_league_size_30_v2", 30)}
                          disabled={saving}
                          className="p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center hover:border-blue-500 transition-all disabled:opacity-50"
                        >
                          <p className="text-blue-400 font-bold text-sm">30</p>
                          <p className="text-slate-500 text-xs">AED 10</p>
                        </button>
                      )}
                      {(league.max_members || 5) < 50 && (
                        <button
                          onClick={() => handleCapacityUpgrade("apl_league_size_50_v2", 50)}
                          disabled={saving}
                          className="p-2 bg-purple-500/10 border border-purple-500/30 rounded-lg text-center hover:border-purple-500 transition-all disabled:opacity-50"
                        >
                          <p className="text-purple-400 font-bold text-sm">50</p>
                          <p className="text-slate-500 text-xs">AED 15</p>
                        </button>
                      )}
                    </div>
                    <p className="text-slate-500 text-xs mt-2 text-center">
                      {isNative ? "Tap to purchase" : "Available in mobile app"}
                    </p>
                    {/* Restore Purchase Button */}
                    <button
                      onClick={handleRestorePurchase}
                      disabled={restoring}
                      className="mt-2 w-full py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/20 transition-all disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid="restore-league-purchase-btn"
                    >
                      {restoring ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                      {restoring ? "Restoring..." : "Restore Purchase"}
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="w-full py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                Save Settings
              </button>

              <hr className="border-slate-700" />

              {/* Transfer Admin */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">Transfer Admin Role</label>
                <select
                  value={newAdminId}
                  onChange={e => setNewAdminId(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                >
                  <option value="">Select a member...</option>
                  {otherMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.team_name || m.display_name}</option>
                  ))}
                </select>
                <button
                  onClick={handleTransferAdmin}
                  disabled={!newAdminId || loading}
                  className="mt-2 w-full py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <ArrowRight size={14} />
                  Transfer Admin
                </button>
              </div>

              <hr className="border-slate-700" />

              {/* Delete League */}
              <button
                onClick={handleDeleteLeague}
                disabled={loading}
                className="w-full py-2 bg-red-600/20 border border-red-500/50 text-red-400 rounded-lg text-sm font-medium hover:bg-red-600/30 transition-all flex items-center justify-center gap-2"
              >
                <Trash2 size={14} />
                Delete League
              </button>
            </div>
          )}
        </div>
      </div>

      {/* User Profile Modal */}
      {selectedProfileUserId && (
        <UserProfileCard 
          userId={selectedProfileUserId} 
          onClose={() => setSelectedProfileUserId(null)} 
        />
      )}
    </div>
  );
}
